import React from 'react';

/**
 * The one progress bar. Every score, coverage, talk-ratio and analysis
 * progress bar in the portal renders through this so they all share one
 * track, one thickness and one fill vocabulary. A bar that needs to look
 * different is a bar that needs a new tone here, not a copy elsewhere.
 */
export type ProgressTone = 'success' | 'warning' | 'error' | 'info' | 'signature' | 'neutral';

/** Only tones the design system's status axis (plus the signature for
 *  "this is ours, not a judgement") may fill a bar. */
const FILL_CLASSES: Record<ProgressTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  info: 'bg-info',
  signature: 'bg-signature',
  neutral: 'bg-ink-disabled',
};

const TEXT_CLASSES: Record<ProgressTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
  info: 'text-info',
  signature: 'text-signature',
  neutral: 'text-ink-disabled',
};

/** Static class lookups, so Tailwind can see every class it must emit. */
export const toneFillClass = (tone: ProgressTone): string => FILL_CLASSES[tone];
export const toneTextClass = (tone: ProgressTone): string => TEXT_CLASSES[tone];

interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 0 to 100; anything outside is clamped. */
  value: number;
  tone: ProgressTone;
}

const clamp = (value: number): number => Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, tone, className = '', ...rest }, ref) => {
    const percent = clamp(value);
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        className={`h-2 w-full overflow-hidden rounded-full bg-surface-subtle ${className}`.trim()}
        {...rest}
      >
        <div
          data-testid="progress-fill"
          className={`h-full rounded-full transition-all duration-500 ${FILL_CLASSES[tone]}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

/**
 * Score to tone, on the same 80 / 50 thresholds as the score badges, so a
 * bar and the chip beside it never disagree. `higherIsBetter` false flips
 * it for qualities where a high score is the problem (defensiveness).
 */
export function scoreTone(score: number, higherIsBetter = true): ProgressTone {
  const effective = higherIsBetter ? score : 100 - score;
  if (effective >= 80) return 'success';
  if (effective >= 50) return 'warning';
  return 'error';
}

export default ProgressBar;
